import { digitAtPower, powersForValue, toSlotRotation } from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import { deriveNaturalMultiplicationTrace } from '@/adapters/primitive/_pure/model/natural/multiplication';
import { resolveNaturalSettlementCells } from '@/adapters/primitive/_pure/model/natural/settlementTrace';
import {
    buildFlightWheel,
    buildNaturalPlaceholderWheel,
    buildOperandWheel,
    buildResultWheel,
    buildWholeFlightWheel,
    dimStableWheelRoles,
    toWheelLayout,
    toWheelPosition,
    type PrimitiveWheelLayout,
} from '@/adapters/primitive/_pure/derivation/wheels';
import { toNaturalReceiverLocationPosition } from '@/adapters/primitive/_pure/derivation/natural/locationProjection';
import { naturalFlightDurationMs } from '@/adapters/primitive/_pure/derivation/natural/naturalFlightAnimation';
import { withMultiplicationComparisons } from '@/adapters/primitive/_pure/derivation/natural/multiplicationComparisons';
import {
    isSettlementPreviewShown,
    toSettlementResolutionSchedule,
    withSettlementResolution,
    type SettlementPreview,
} from '@/adapters/primitive/_pure/derivation/natural/settlementPreview';
import { projectNaturalTraceToStepStates } from '@/adapters/primitive/_pure/derivation/natural/traceProjection';
import { momentKey, momentPart, stepMoment, withMomentFrame, withMomentParameters } from '@/adapters/primitive/_pure/derivation/primitiveMoment';
import type { DirectionCatalog } from '@/_contract/directionRule';
import type { PrimitiveOperationDirectionEffect } from '@/adapters/primitive/_contract/_~generated/operationDirectionEffect';
import type { NaturalEventEnvelope } from '@/adapters/primitive/_contract/natural/event';
import type { NaturalReceiverLocation } from '@/adapters/primitive/_contract/natural/location';
import type { NaturalMachineState } from '@/adapters/primitive/_contract/natural/machine';
import type {
    Config,
    PrimitiveWheelRole,
    PrimitiveWheelState,
} from '@/adapters/primitive/_contract/config';
import type { PrimitiveKeyframeTransition, PrimitiveStepState } from '@/adapters/primitive/_contract/steps';
import type { NaturalMultiplicationTrace } from '@/adapters/primitive/_contract/trace';

function remapRole(role: PrimitiveWheelRole): PrimitiveWheelRole {
    switch (role) {
        case 'addend-one':
        case 'sum':
            return 'product';
        case 'addend-two':
            return 'multiplicand';
        case 'multiplier':
            return 'remaining-repetitions';
        default:
            return role;
    }
}

function remapEnvelope(envelope: NaturalEventEnvelope): NaturalEventEnvelope {
    const event = envelope.event;
    return {
        ...envelope,
        event: {
            ...event,
            ...('contributionTraveller' in event
                ? { contributionTraveller: { ...event.contributionTraveller, wheelRole: remapRole(event.contributionTraveller.wheelRole) } }
                : {}),
            ...('location' in event
                ? { location: { ...event.location, receiverRole: remapRole(event.location.receiverRole) } }
                : {}),
            ...('receiverInteraction' in event
                ? {
                    receiverInteraction: {
                        ...event.receiverInteraction,
                        receiver: {
                            ...event.receiverInteraction.receiver,
                            wheelRole: remapRole(event.receiverInteraction.receiver.wheelRole),
                        },
                    },
                }
                : {}),
        } as NaturalEventEnvelope['event'],
    };
}

function valueWheel(args: {
    layout: PrimitiveWheelLayout;
    role: 'multiplier' | 'remaining-repetitions' | 'product';
    value: number;
    wheelOpacity?: number;
}): PrimitiveWheelState {
    const powers = powersForValue(args.value);
    return {
        ...buildResultWheel({
            layout: args.layout,
            role: args.role,
            powers,
            ...(args.wheelOpacity === undefined ? {} : { wheelOpacity: args.wheelOpacity }),
            countedThroughDigitByPower: Object.fromEntries(
                powers.map((power) => [power, digitAtPower({ value: args.value, power })]),
            ),
        }),
        ...(args.role === 'remaining-repetitions'
            ? { helperWheel: { key: 'remaining-repetitions', kind: 'Counter' as const } }
            : {}),
    };
}

function buildStableWheels(args: {
    config: Config;
    layout: PrimitiveWheelLayout;
    multiplicand: number;
    multiplier: number;
    residual: number;
    product: number;
    multiplicandSpent?: boolean;
    remainingOpacity?: number;
    omitRemaining?: boolean;
    completed?: boolean;
    productPlaceholder?: boolean;
}): readonly PrimitiveWheelState[] {
    const wheels = [
        valueWheel({
            layout: args.layout,
            role: 'multiplier',
            value: args.multiplier,
        }),
        ...(args.omitRemaining ? [] : [valueWheel({
            layout: args.layout,
            role: 'remaining-repetitions',
            value: args.residual,
            wheelOpacity: args.remainingOpacity,
        })]),
        buildOperandWheel({
            layout: args.layout,
            role: 'multiplicand',
            value: args.multiplicand,
            ...(args.multiplicandSpent
                ? {
                    bandOpacityByPower: Object.fromEntries(
                        powersForValue(args.multiplicand)
                            .map((power) => [power, args.config.natural.display.band.spentOpacity]),
                    ),
                }
                : {}),
        }),
        args.productPlaceholder
            ? buildNaturalPlaceholderWheel({ layout: args.layout, role: 'product' })
            : valueWheel({ layout: args.layout, role: 'product', value: args.product }),
    ];
    return args.completed
        ? dimStableWheelRoles({
            config: args.config,
            wheels,
            roles: ['multiplicand'],
        })
        : wheels;
}

function remapContributionStep(args: {
    config: Config;
    layout: PrimitiveWheelLayout;
    multiplicand: number;
    multiplier: number;
    step: PrimitiveStepState;
    cycleIndex: number;
    residual: number;
    productBefore: number;
    product: number;
}): PrimitiveStepState {
    const fixed = buildStableWheels(args);
    return {
        ...withMomentFrame(args.step, momentPart('contribute', undefined, args.cycleIndex)),
        label: args.step.label.replaceAll('AddendTwo', 'Multiplicand').replaceAll('Sum', 'Product'),
        description: args.step.description
            .replaceAll('AddendTwo', 'Multiplicand')
            .replaceAll('Sum', 'Product'),
        keyframes: args.step.keyframes.map((keyframe) => {
            const projected = keyframe.wheels.flatMap((wheel): PrimitiveWheelState[] => {
                if (wheel.variant === undefined && wheel.role === 'addend-one') {
                    return [];
                }
                if (wheel.variant === undefined
                    && wheel.role === 'sum'
                    && wheel.valueKind === 'placeholder'
                    && args.cycleIndex > 1) {
                    return [valueWheel({
                        layout: args.layout,
                        role: 'product',
                        value: args.productBefore,
                    })];
                }
                return [{
                    ...wheel,
                    role: remapRole(wheel.role),
                    ...(wheel.receiverLocation === undefined
                        ? {}
                        : {
                            receiverLocation: {
                                ...wheel.receiverLocation,
                                receiverRole: remapRole(wheel.receiverLocation.receiverRole),
                            },
                        }),
                }];
            });
            const projectedRoles = new Set(
                projected.filter((wheel) => wheel.variant === undefined).map((wheel) => wheel.role),
            );
            return {
                ...keyframe,
                wheels: [...fixed.filter((wheel) => !projectedRoles.has(wheel.role)), ...projected],
                events: keyframe.events.map(remapEnvelope),
            };
        }),
    };
}

function remainingFromState(layout: PrimitiveWheelLayout, state: NaturalMachineState): PrimitiveWheelState {
    const wheel = state.wheels.find((candidate) => candidate.role === 'multiplier');
    const bands = wheel?.bands ?? [];
    return {
        ...buildResultWheel({
            layout,
            role: 'remaining-repetitions',
            powers: bands.map((band) => band.power),
            countedThroughDigitByPower: Object.fromEntries(
                bands.map((band) => [band.power, band.countedSlots.length]),
            ),
        }),
        helperWheel: { key: 'remaining-repetitions', kind: 'Counter' },
    };
}

function decrementTransients(
    config: Config,
    layout: PrimitiveWheelLayout,
    state: NaturalMachineState,
    options?: { position?: [number, number, number]; location?: NaturalReceiverLocation; opacity?: number; entranceGrowth?: number },
): PrimitiveWheelState[] {
    const multiplication = state.multiplication;
    const unit = multiplication?.unit;
    const assembly = multiplication?.borrowAssembly;
    const remapLocation = (location: NaturalReceiverLocation): NaturalReceiverLocation => ({
        ...location, receiverRole: remapRole(location.receiverRole),
    });
    const transientPosition = (location: Parameters<typeof toNaturalReceiverLocationPosition>[0]['location']) => (
        options?.position ?? toNaturalReceiverLocationPosition({ config, layout, location: remapLocation(location) })
    );
    const transientLocation = (location: NaturalReceiverLocation) => options?.location ?? remapLocation(location);
    const opacityForPower = (power: number) => options?.opacity === undefined
        ? undefined
        : { [power]: options.opacity };
    return [
        ...(unit === null || unit === undefined ? [] : [{
            ...buildFlightWheel({
                role: 'remaining-repetitions',
                value: 1,
                power: unit.band.power,
                position: transientPosition(unit.location),
                rotation: toSlotRotation({ slotOffset: unit.slotOffset, radix: state.radix }),
                countedDigits: unit.band.countedSlots,
                overhangDigits: unit.band.overhangSlots,
                receiverLocation: transientLocation(unit.location),
            }),
            helperWheel: { key: 'unit:remaining-repetitions', kind: 'Unit' as const },
            bandOpacityByPower: opacityForPower(unit.band.power),
            ...(options?.entranceGrowth === undefined ? {} : { entranceGrowth: options.entranceGrowth }),
        }]),
        ...(assembly === null || assembly === undefined ? [] : [
            ...assembly.lowerBands.map((member) => ({
                ...buildFlightWheel({
                    variant: member.memberKind === 'radix-band' ? 'radix-band' : 'flight',
                    role: 'remaining-repetitions',
                    value: state.radix,
                    power: member.band.power,
                    position: transientPosition(assembly.location),
                    countedDigits: member.band.countedSlots,
                    overhangDigits: member.band.overhangSlots,
                    receiverLocation: transientLocation(assembly.location),
                }),
                bandOpacityByPower: opacityForPower(member.band.power),
            })),
            {
                ...buildFlightWheel({
                    role: 'remaining-repetitions',
                    value: 1,
                    power: assembly.outerUnit.band.power,
                    position: transientPosition(assembly.location),
                    rotation: toSlotRotation({ slotOffset: assembly.outerUnit.slotOffset, radix: state.radix }),
                    countedDigits: assembly.outerUnit.band.countedSlots,
                    overhangDigits: assembly.outerUnit.band.overhangSlots,
                    receiverLocation: transientLocation(assembly.location),
                }),
                bandOpacityByPower: opacityForPower(assembly.outerUnit.band.power),
            },
        ]),
    ];
}

function decrementTransition(
    config: Config,
    eventKind: NaturalEventEnvelope['event']['eventKind'],
): PrimitiveKeyframeTransition {
    switch (eventKind) {
        case 'unit-introduced':
        case 'unit-aligned':
        case 'borrow-assembly-layer-introduced':
        case 'borrow-assembly-moved':
            return { durationMs: config.base.animation?.flightBaseDurationMs ?? 0 };
        case 'unit-crossed-zero':
        case 'borrow-assembly-outer-unit-aligned':
        case 'borrow-assembly-outer-unit-crossed-zero':
            return { durationMs: config.base.animation?.rotatePerSlotDurationMs ?? 0 };
        case 'receiver-bands-settled':
            return { durationMs: config.base.animation?.fadeDurationMs ?? 0 };
        default:
            return 'instant';
    }
}

function decrementLabel(eventKind: NaturalEventEnvelope['event']['eventKind']): string {
    switch (eventKind) {
        case 'unit-introduced': return 'Introduce Counter Unit';
        case 'unit-aligned': return 'Align Counter Unit';
        case 'unit-crossed-zero': return 'Cross Counter Zero';
        case 'borrow-assembly-layer-introduced': return 'Introduce Borrow Layer';
        case 'borrow-assembly-moved': return 'Move Borrow Assembly';
        case 'borrow-assembly-outer-unit-aligned': return 'Align Borrow Unit';
        case 'borrow-assembly-outer-unit-crossed-zero': return 'Cross Borrow Zero';
        case 'receiver-bands-settled': return 'Settle Counter';
        case 'unit-decrement-completed': return 'Complete Counter Decrement';
        default: return 'Update Counter';
    }
}

export function deriveNaturalMultiplicationStepStates(args: {
    config: Config;
    multiplier: number;
    multiplicand: number;
}): readonly PrimitiveStepState[] {
    return projectNaturalMultiplicationTrace({ ...args, trace: deriveNaturalMultiplicationTrace({
        multiplier: args.multiplier, multiplicand: args.multiplicand, radix: args.config.natural.radix,
    }) });
}

export function projectNaturalMultiplicationTrace(args: {
    config: Config;
    directionCatalog?: DirectionCatalog<PrimitiveOperationDirectionEffect>;
    multiplier: number;
    multiplicand: number;
    trace: NaturalMultiplicationTrace;
    splitZeroCrossing?: boolean;
    splitProductSettlement?: boolean;
    fullComparisons?: number;
}): readonly PrimitiveStepState[] {
    const { multiplicand } = args;
    const config = args.config;
    const layout = toWheelLayout({ config, operandSchedulingMode: 'grouped' });
    const compactMultiplierPosition = toWheelPosition({ layout, role: 'multiplier' });
    compactMultiplierPosition[2] -= layout.wheelGap;
    const stableWheels = (options: Omit<Parameters<typeof buildStableWheels>[0], 'multiplier' | 'multiplicand' | 'layout'>) => (
        buildStableWheels({ ...options, multiplier: args.multiplier, multiplicand, layout })
    );
    const multiplication = args.trace;
    const compact = (wheels: readonly PrimitiveWheelState[]) => wheels.map(wheel => wheel.role === 'multiplier'
        ? { ...wheel, position: compactMultiplierPosition } : wheel);
    const steps: PrimitiveStepState[] = [{
        ...momentKey(stepMoment('initial')),
        label: 'Start',
        description: 'Multiplier, Multiplicand, and an empty Product are ready.',
        keyframes: [{
            wheels: compact(stableWheels({
                config,
                residual: multiplication.multiplier,
                product: 0,
                completed: multiplication.cycles.length === 0,
                productPlaceholder: multiplication.cycles.length > 0,
                omitRemaining: true,
            })),
            transition: 'instant',
            events: [],
        }],
    }];

    {
        const wheels = stableWheels({
            config, residual: multiplication.multiplier, product: 0, productPlaceholder: multiplication.cycles.length > 0,
        });
        const withoutHelper = wheels.filter(wheel => wheel.role !== 'remaining-repetitions');
        steps.push({
            ...momentKey(stepMoment('rod-extend')),
            label: 'Make Room for Counter',
            description: 'Extend the rod before the countdown copy arrives.',
            cameraFocus: { stationKey: 'overview', viewKey: 'overview',
                roles: ['multiplier', 'multiplicand', 'product'] },
            keyframes: [
                { wheels: compact(withoutHelper), transition: 'instant', events: [] },
                { wheels: withoutHelper, transition: { durationMs: config.base.animation?.flightBaseDurationMs ?? 0 }, events: [] },
            ],
        });
        const copyAt = (role: 'multiplier' | 'remaining-repetitions'): readonly PrimitiveWheelState[] => [
            ...wheels.filter((wheel) => wheel.role !== 'remaining-repetitions'),
            {
                ...buildWholeFlightWheel({
                    role: 'remaining-repetitions',
                    value: multiplication.multiplier,
                    bands: powersForValue(multiplication.multiplier).map((power) => ({
                        power,
                        countedSlots: Array.from(
                            { length: digitAtPower({ value: multiplication.multiplier, power }) },
                            (_, index) => index + 1,
                        ),
                        overhangSlots: [],
                        marker: null,
                    })),
                    position: toWheelPosition({ layout, role }),
                }),
                helperWheel: { key: 'remaining-repetitions', kind: 'Counter' },
                receiverLocation: { receiverRole: role, locationKind: 'receiver' },
            },
        ];
        steps.push({
            ...momentKey(stepMoment('remaining-repetitions-copy')),
            label: 'Copy Counter',
            description: 'Copy the whole Multiplier to Counter. The original input stays unchanged.',
            keyframes: [
                {
                    wheels: copyAt('multiplier'),
                    transition: 'instant',
                    events: [],
                },
                {
                    wheels: copyAt('remaining-repetitions'),
                    transition: { durationMs: naturalFlightDurationMs({
                        config,
                        from: toWheelPosition({ layout, role: 'multiplier' }),
                        to: toWheelPosition({ layout, role: 'remaining-repetitions' }),
                    }) },
                    events: [],
                },
                { wheels, transition: 'instant', events: [] },
            ],
        });
    }

    for (const cycle of multiplication.cycles) {
        const contributionSteps = projectNaturalTraceToStepStates({
            config,
            operandSchedulingMode: 'grouped',
            operation: 'addition',
            operandOne: cycle.productBefore,
            operandTwo: multiplication.multiplicand,
            trace: cycle.contributionTrace,
            dimTerminalOperands: false,
            splitZeroCrossing: args.splitZeroCrossing,
        }).steps;
        const projectedContributionSteps = contributionSteps.slice(1).map((step) => remapContributionStep({
            config,
            layout,
            multiplicand,
            multiplier: args.multiplier,
            step,
            cycleIndex: cycle.cycleIndex,
            residual: cycle.residualBefore,
            productBefore: cycle.productBefore,
            product: cycle.productAfter,
        }));
        const introduction = cycle.decrementTrace.points[0];
        if (introduction?.event.event.eventKind !== 'unit-introduced') {
            throw new Error('Natural multiplication decrement is missing Unit introduction.');
        }
        const alignment = cycle.decrementTrace.points[1];
        if (alignment?.event.event.eventKind !== 'unit-aligned') {
            throw new Error('Natural multiplication decrement is missing Unit alignment.');
        }
        const alignmentIsAutomatic = alignment.event.event.slotOffset === 0;
        const finalContributionStep = projectedContributionSteps.at(-1);
        if (finalContributionStep === undefined) {
            throw new Error('Natural multiplication cycle is missing Product contribution steps.');
        }
        const introductionWheels = stableWheels({
            config,
            residual: cycle.residualBefore,
            product: cycle.productAfter,
            multiplicandSpent: true,
        });
        const hiddenUnit = decrementTransients(config, layout, introduction.nextState, { opacity: 0, entranceGrowth: 0 });
        const finalContributionKeyframe = finalContributionStep.keyframes.at(-1)!;
        const splitProductSettlement = args.splitProductSettlement && cycle.cycleIndex > 1;
        if (splitProductSettlement) {
            projectedContributionSteps[projectedContributionSteps.length - 1] = {
                ...withMomentParameters(finalContributionStep, { settlement: 'product' }),
                keyframes: [
                    ...finalContributionStep.keyframes.slice(0, -1),
                    { ...finalContributionKeyframe, wheels: [...finalContributionKeyframe.wheels, ...hiddenUnit] },
                ],
            };
        }
        const withUnit: PrimitiveStepState = {
            ...finalContributionStep,
            keyframes: [
                ...(splitProductSettlement ? [] : finalContributionStep.keyframes.slice(0, -1)),
                // Keep the hint ring at settlement; episodes may inspect Product before revealing the Unit.
                { ...finalContributionKeyframe, wheels: [...finalContributionKeyframe.wheels, ...hiddenUnit],
                    ...(splitProductSettlement ? { transition: 'instant' as const, events: [] } : {}) },
                {
                    wheels: [...introductionWheels, ...hiddenUnit],
                    transition: 'instant',
                    events: [remapEnvelope(introduction.event)],
                },
                {
                    wheels: [
                        ...introductionWheels,
                        ...decrementTransients(
                            config,
                            layout,
                            alignmentIsAutomatic ? alignment.nextState : introduction.nextState,
                            { opacity: 1, entranceGrowth: 1 },
                        ),
                    ],
                    transition: { durationMs: config.base.animation?.fadeDurationMs ?? 0 },
                    events: alignmentIsAutomatic ? [remapEnvelope(alignment.event)] : [],
                },
            ],
        };
        if (splitProductSettlement) projectedContributionSteps.push(withUnit);
        else projectedContributionSteps[projectedContributionSteps.length - 1] = withUnit;
        steps.push(...projectedContributionSteps);

        for (
            let pointIndex = alignmentIsAutomatic ? 2 : 1;
            pointIndex < cycle.decrementTrace.points.length;
            pointIndex += 1
        ) {
            const point = cycle.decrementTrace.points[pointIndex];
            const eventKind = point.event.event.eventKind;
            if (eventKind === 'unit-decrement-completed') continue;
            if (eventKind === 'receiver-bands-settled') {
                const completion = cycle.decrementTrace.points[pointIndex + 1];
                if (completion?.event.event.eventKind !== 'unit-decrement-completed') {
                    throw new Error('Natural multiplication decrement settlement is missing completion.');
                }
                const receiverPosition = toNaturalReceiverLocationPosition({
                    config,
                    layout,
                    location: { receiverRole: 'remaining-repetitions', locationKind: 'receiver' },
                });
                const receiverLocation: NaturalReceiverLocation = { receiverRole: 'remaining-repetitions', locationKind: 'receiver' };
                const stableBefore = [
                    remainingFromState(layout, point.previousState),
                    ...stableWheels({
                        config,
                        residual: cycle.residualBefore,
                        product: cycle.productAfter,
                        multiplicandSpent: true,
                    }).filter((wheel) => wheel.role === 'multiplicand' || wheel.role === 'multiplier'),
                    valueWheel({ layout, role: 'product', value: cycle.productAfter }),
                ];
                const stableAfterSpent = [
                    remainingFromState(layout, point.nextState),
                    ...stableWheels({
                        config,
                        residual: cycle.residualAfter,
                        product: cycle.productAfter,
                        multiplicandSpent: true,
                    }).filter((wheel) => wheel.role === 'multiplicand' || wheel.role === 'multiplier'),
                    valueWheel({ layout, role: 'product', value: cycle.productAfter }),
                ];
                const atReceiver = decrementTransients(config, layout, point.previousState, {
                    position: receiverPosition,
                    location: receiverLocation,
                });
                const lowerMembers = point.previousState.multiplication?.borrowAssembly?.lowerBands ?? [];
                const preview: SettlementPreview = {
                    settlements: resolveNaturalSettlementCells(point),
                    incoming: (index) => ({
                        role: 'remaining-repetitions',
                        variant: lowerMembers[index]?.memberKind === 'radix-band' ? 'radix-band' : 'flight',
                    }),
                    receiver: () => ({ role: 'remaining-repetitions' }),
                };
                const hover = [...stableBefore, ...decrementTransients(config, layout, point.previousState)];
                const previewShown = isSettlementPreviewShown({ config, wheels: hover, preview });
                const previewConfig = config.natural.display.settlementPreview;
                const previewed = (wheels: readonly PrimitiveWheelState[]) => (
                    !previewShown
                        ? wheels
                        : withSettlementResolution({ config, wheels, preview, resolution: 'all' })
                );
                const previousStep = steps[steps.length - 1];
                const previousKeyframe = previousStep.keyframes[previousStep.keyframes.length - 1];
                if (previewShown) {
                    steps.push({
                        ...withMomentFrame(previousStep, momentPart('composite')),
                        label: 'Inspect Settlement',
                        description: 'Inspect the Counter decrement participants and resolve their cells before applying it.',
                        keyframes: [
                            {
                                wheels: withSettlementResolution({ config, wheels: previousKeyframe.wheels, preview, resolution: 'entry' }),
                                transition: 'instant' as const,
                                events: [],
                            },
                            ...toSettlementResolutionSchedule({ config: previewConfig, preview }).map(({ resolution, durationMs }) => ({
                                wheels: withSettlementResolution({ config, wheels: previousKeyframe.wheels, preview, resolution }),
                                transition: { durationMs },
                                events: [],
                            })),
                        ],
                    });
                }
                steps.push({
                    ...momentKey(stepMoment('decrement', { sequence: point.event.sequence, event: eventKind }, cycle.cycleIndex)),
                    label: decrementLabel(eventKind),
                    description: `Product contribution is complete; decrement Counter from ${cycle.residualBefore} to ${cycle.residualAfter}. The Multiplier stays unchanged.`,
                    keyframes: [
                        {
                            wheels: previewed([...stableBefore, ...atReceiver]),
                            transition: { durationMs: config.base.animation?.flightBaseDurationMs ?? 0 },
                            events: [],
                        },
                        { wheels: stableAfterSpent, transition: 'instant', events: [remapEnvelope(point.event)] },
                        { wheels: stableAfterSpent, transition: 'instant', events: [remapEnvelope(completion.event)] },
                    ],
                });
                pointIndex += 1;
                continue;
            }
            steps.push({
                ...momentKey(stepMoment('decrement', { sequence: point.event.sequence, event: eventKind }, cycle.cycleIndex)),
                label: decrementLabel(eventKind),
                description: `Product contribution is complete; decrement Counter from ${cycle.residualBefore} to ${cycle.residualAfter}. The Multiplier stays unchanged.`,
                keyframes: [{
                    wheels: [
                        remainingFromState(layout, point.nextState),
                        ...stableWheels({
                            config,
                            residual: cycle.residualBefore,
                            product: cycle.productAfter,
                            multiplicandSpent: true,
                        }).filter((wheel) => wheel.role === 'multiplicand' || wheel.role === 'multiplier'),
                        valueWheel({ layout, role: 'product', value: cycle.productAfter }),
                        ...decrementTransients(config, layout, point.nextState),
                    ],
                    transition: decrementTransition(config, eventKind),
                    events: [remapEnvelope(point.event)],
                }],
            });
        }
    }

    {
        const zero = stableWheels({ config, residual: 0, product: multiplication.product, completed: true });
        const faded = stableWheels({ config, residual: 0, product: multiplication.product, completed: true, remainingOpacity: 0 });
        const remaining = stableWheels({ config, residual: 0, product: multiplication.product, completed: true, omitRemaining: true });
        steps.push({
            ...momentKey(stepMoment('remaining-repetitions-remove')), label: 'Remove Exhausted Helper',
            description: 'The zero countdown wheel disappears after all contributions have settled.',
            keyframes: [
                { wheels: zero, transition: 'instant', events: [] },
                { wheels: zero, transition: { durationMs: config.natural.camera.dispatchHoldDurationMs }, events: [] },
                { wheels: faded, transition: { durationMs: config.base.animation?.fadeDurationMs ?? 0 }, events: [] },
                { wheels: remaining, transition: 'instant', events: [] },
            ],
        }, {
            ...momentKey(stepMoment('rod-contract')), label: 'Restore Normal Rod',
            description: 'Close the temporary helper station; the original operands and Product remain.',
            cameraFocus: { stationKey: 'overview', viewKey: 'overview', roles: ['multiplier', 'multiplicand', 'product'] },
            keyframes: [
                { wheels: remaining, transition: 'instant', events: [] },
                { wheels: compact(remaining), transition: { durationMs: config.base.animation?.flightBaseDurationMs ?? 0 }, events: [] },
            ],
        });
    }
    const copyIndex = steps.findIndex(step => step.stepKey === 'remaining-repetitions-copy');
    const projected = steps.map((step, stepIndex) => ({ ...step, keyframes: step.keyframes.map((keyframe, index) => {
        const wheels = copyIndex >= 0 && stepIndex >= copyIndex
            ? dimStableWheelRoles({ config, wheels: keyframe.wheels, roles: ['multiplier'] })
            : keyframe.wheels;
        const isCompact = step.stepKey === 'initial'
            || (step.stepKey === 'rod-extend' && index === 0)
            || (step.stepKey === 'rod-contract' && index === step.keyframes.length - 1);
        if (isCompact) return { ...keyframe, wheels };
        return { ...keyframe, wheels: wheels.map(wheel => ({
            ...wheel, position: [wheel.position[0], wheel.position[1],
                wheel.position[2] - layout.wheelGap] as [number, number, number],
        })) };
    }) }));
    return withMultiplicationComparisons({ config, steps: projected, trace: multiplication, directionCatalog: args.directionCatalog,
        fullComparisons: args.fullComparisons });
}